import classNames from 'classnames';
import { lazy, Suspense, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import { useLanguageByUser, useTranslator } from '@picsart/rc/services/localization';

import { AnalyticEventTypes } from 'constants/enums';
import { analyticModule, GTM_EXPERIMENT_URLS } from 'constants/main';
import useAnalytics from 'services/Analytics';

import Header from 'components/Header';

import getHrefLangs from 'utils/hreflangs';

import usePageSlug from 'hooks/usePageSlug';
import useLandingPropsStore from 'stores/landingPropsStore';

import useMount from '@picsart/rc/useMount';
import Breadcrumb from 'components/Breadcrumb';
import { SchemaType } from 'components/JSONLinkedData/types';
import type { LayoutType } from './types';

import useStyles from './styles';

const LanguageChooser = lazy(
  () =>
    import(/* webpackChunkName: "LanguageChooser" */ 'components/LanguageChooser/LanguageChooser'),
);

const JSONLinkedData = lazy(
  () => import(/* webpackChunkName: "JSONLinkedData" */ 'components/JSONLinkedData'),
);

const SmallFooter = lazy(
  () => import(/* webpackChunkName: "SmallFooter" */ 'components/Footer/SmallFooter'),
);

const Footer = lazy(
  () => import(/* webpackChunkName: "Footer", webpackPrefetch: true */ 'components/Footer'),
);

function Layout({
  title,
  metaDescription = '',
  ogDescription = '',
  miniNavigation,
  keywords = '',
  ogTitle = '',
  ogImage,
  buttonProps,
  ogUrl,
  name = '',
  localized,
  customFooter,
  children,
  isMobile,
  noIndex,
  noFollow,
  schemas,
  imageSrc,
  canonicalUrl,
  twitterImage,
  twitterTitle,
  twitterDescription,
  isLocalized,
  isLandingOrHub,
  hreflangLanguages: languages,
  footerRelevantLinks,
  footerRelevantLinkEnabled,
  hasDefaultBreadcrumb = true,
  hideHeaderAndFooterNavigations,
  logoBehavior,
}: LayoutType) {
  const translate = useTranslator();
  const { slug: pagePath } = usePageSlug();
  const { currentLanguage } = useLanguageByUser();
  const { root, row, section } = useStyles();
  const hideHeader = isMobile && GTM_EXPERIMENT_URLS.includes(pagePath);

  const { fireEvent } = useAnalytics();
  const metaOgUrl = ogUrl || window.location.href;
  const category = useLandingPropsStore(x => x.category);
  const screenName = useLandingPropsStore(x => x.screenName);
  const ogImg = category === 'color' ? imageSrc : ogImage || imageSrc;
  const fireAnalyticEvent = useCallback(() => {
    fireEvent(AnalyticEventTypes.page_click, {
      category,
      action_button: buttonProps.buttonType,
      screen_name: screenName,
      item_name: 'main-header',
      module: analyticModule,
    });
  }, [category, screenName, buttonProps, fireEvent]);

  useMount(() => {
    fireEvent(AnalyticEventTypes.page_view, {
      platform: 'Website',
      page_category: 'Landing',
      page_name: pagePath,
    });
  });

  const handleClick = useCallback(() => {
    fireAnalyticEvent();
    if (isMobile) {
      window.location.href = buttonProps.mobileUrl;
    }
  }, [fireAnalyticEvent, isMobile, buttonProps]);
  const robots = useMemo(() => {
    const r = [];

    if (noIndex) {
      r.push('noindex');
    }
    if (noFollow) {
      r.push('nofollow');
    }
    return r.join(', ');
  }, [noIndex, noFollow]);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="keywords" content={keywords} />
        <title>{translate(title || name)}</title>
        <meta name="description" content={translate(metaDescription)} />
        <meta
          name="viewport"
          content="width=device-width, user-scalable=no, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0"
        />
        <meta httpEquiv="X-UA-Compatible" content="ie=edge" />
        <meta property="og:title" content={translate(ogTitle || title || name)} />
        <meta property="og:description" content={translate(ogDescription || metaDescription)} />
        <meta property="og:site_name" content="Picsart" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={metaOgUrl} />
        {ogImg && <meta property="og:image" content={translate(ogImg)} />}
        {(twitterImage || imageSrc) && (
          <meta name="twitter:image" content={twitterImage || imageSrc} />
        )}
        <meta name="twitter:card" content="photo" />
        <meta name="twitter:site" content="@picsart" />
        <meta name="twitter:title" content={translate(twitterTitle || ogTitle || title || name)} />
        <meta
          name="twitter:description"
          content={translate(twitterDescription || ogDescription || metaDescription)}
        />
        {robots?.length > 0 && <meta name="robots" content={robots} />}
        {getHrefLangs(languages, currentLanguage, pagePath, isLocalized, canonicalUrl).map(h => (
          <link
            rel={h.rel}
            href={h.href}
            key={h.hreflang || h.rel}
            {...(h.hreflang ? { hrefLang: h.hreflang } : {})}
          />
        ))}
      </Helmet>
      <div className={root}>
        {!hideHeader && (
          <div className={classNames(row, 'top')}>
            {/* Don't remove header-content id: it's used by src/hooks/useHeaderHeight.ts */}
            <div className={classNames(section, 'header')} id="header-content">
              {miniNavigation ? (
                <Header miniNavigation={miniNavigation} onClick={handleClick} />
              ) : (
                <Header
                  isLandingOrHub={isLandingOrHub}
                  hideHeaderAndFooterNavigations={hideHeaderAndFooterNavigations}
                  logoBehavior={logoBehavior}
                />
              )}
            </div>
          </div>
        )}
        {hasDefaultBreadcrumb && <Breadcrumb />}
        <div className={classNames(row, 'middle')}>
          <div className={classNames(section, 'main')}>
            {localized && (
              <Suspense fallback={null}>
                <LanguageChooser localized={localized} />
              </Suspense>
            )}
            {children}
          </div>
        </div>
        <div className={classNames(row, 'bottom')}>
          <div className={classNames(section, 'footer')}>
            <Suspense fallback={null}>
              {customFooter ? (
                <SmallFooter isMobile={isMobile} onClick={handleClick} />
              ) : (
                <Footer
                  relevantLinkEnabled={footerRelevantLinkEnabled}
                  isLocalized={isLocalized}
                  relevantLinks={footerRelevantLinks}
                  hideHeaderAndFooterNavigations={hideHeaderAndFooterNavigations}
                />
              )}
            </Suspense>
          </div>
        </div>
        {schemas?.map(key => (
          <Suspense fallback={null} key={key['@type']}>
            <JSONLinkedData data={key} type={SchemaType.Custom} />
          </Suspense>
        ))}
      </div>
    </>
  );
}

export default Layout;
